import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { getCurrentSession, loginWithBluesky, logout as apiLogout, Session, User } from '../services/api'

interface AuthContextType {
  session: Session | null
  user: User | null
  loading: boolean
  login: (uid: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  const checkAuth = async () => {
    try {
      setLoading(true)
      const sessionData = await getCurrentSession()
      setSession(sessionData)
    } catch (error) {
      console.error('Failed to check auth:', error)
      setSession(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (uid: string, password: string) => {
    try {
      await loginWithBluesky(uid, password)
      // After successful login, fetch the session
      await checkAuth()
    } catch (error) {
      throw error
    }
  }

  const logout = async () => {
    try {
      await apiLogout()
      setSession(null)
    } catch (error) {
      console.error('Logout failed:', error)
      // Clear session anyway
      setSession(null)
    }
  }

  // Check auth on mount
  useEffect(() => {
    checkAuth()
  }, [])

  const value: AuthContextType = {
    session,
    user: session?.user || null,
    loading,
    login,
    logout,
    checkAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

